import { useState, type CSSProperties, useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  useProject, usePhases, useItemsForProject, useToggleItem, useAllSubtasks, subtaskCounts,
  useSetItemStatus, useUpdateItem, useProjects,
} from "@/hooks/useData";
import { HeaderTabs } from "@/components/HeaderTabs";
import { pct, dueLabel, plusWeek } from "@/lib/format";
import type { Item, Phase } from "@/lib/types";
import { STATUS_ORDER, STATUS_META, type ItemStatus, TYPE_META } from "@/lib/types";
import { StatusPill, ProgressBar, card } from "@/components/atoms";
import { useIsMobile } from "@/lib/useMediaQuery";
import { Kanban, PhaseChip, type Column } from "@/components/Kanban";
import { buildHierarchy, progressCounts, todoistNesting } from "@/lib/hierarchy";
import { useTaskEditor } from "@/context/TaskEditorContext";
import { loadDefaultTab, loadBoardGrouping, useShowCompleted } from "@/lib/theme";
import { ProjectModal } from "@/components/ProjectModal";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { TimelineTab, ProjectCalendarTab, NotesTab, AboutTab, ClientReportTab, AnalyticsTab, MatrixTab, StructureTab } from "@/components/ProjectTabs";
import { GridTab } from "@/components/GridTab";
import { useToast } from "@/context/ToastContext";
import { useChrome, useSearchQuery, matchesQuery } from "@/context/ChromeContext";
import { useTimeEntries } from "@/hooks/useTime";
import { budgetStatus, hours } from "@/lib/timeRollup";

// §9 — nine tabs become six. Everything reference-like moves behind Overview's
// segmented control; all four panels survive, only the routing above them changes.
const TABS = ["Board", "Grid", "Timeline", "Calendar", "Notes", "Overview"];

// §15 — the phone gets two tabs. Timeline, Calendar, Analytics, Matrix and
// About are desktop work, and nothing routes to them here: no placeholder
// screen, because a tab that opens an apology is worse than no tab.
const MOBILE_TABS = ["Items", "Client report"];

const OVERVIEW_SEGMENTS: [string, string][] = [
  ["definition", "Definition"],
  ["structure", "Structure"],
  ["analytics", "Analytics"],
  ["report", "Client report"],
];

export function ProjectDetail() {
  const { id } = useParams();
  const { data: project } = useProject(id);
  const { data: allProjects = [] } = useProjects();
  const { data: phases = [] } = usePhases(id);
  const { data: items = [] } = useItemsForProject(id);
  const toggle = useToggleItem();
  const setStatus = useSetItemStatus();
  const updateItem = useUpdateItem();
  const { data: subs = [] } = useAllSubtasks();
  const subCounts = progressCounts(items, subtaskCounts(subs));
  const { parentOf } = buildHierarchy(items);
  const toast = useToast();
  const mobile = useIsMobile();
  const query = useSearchQuery();

  // §10 — "+ Project" and the templates land here, on Overview › Definition,
  // with the name focused so the first thing you can do is name the thing.
  const openDefinition = !!(useLocation().state as { openDefinition?: boolean } | null)?.openDefinition;
  const mobileFirst = useIsMobile();
  const [rawTab, setTab] = useState<string>(
    mobileFirst ? "Items" : openDefinition ? "Overview" : loadDefaultTab()
  );
  const [overview, setOverview] = useState("definition");
  // Structure is the default outline; the old phase × status crosstab stays as
  // its second segment, because it answers a different question.
  const [structureView, setStructureView] = useState("structure");
  const [group, setGroup] = useState<"phase" | "status">(loadBoardGrouping);
  // Ordering inside a column was invisible: it has always been `position`, with
  // no way to see that or change it. Now it is stated, and manual is editable.
  const [sortBy, setSortBy] = useState<"manual" | "due" | "priority">("manual");
  // The same preference the Grid toolbar and Settings write — one setting, not
  // a third switch that can disagree with the other two.
  const [showCompleted, setShowCompleted] = useShowCompleted();

  const { data: entries = [] } = useTimeEntries();
  const [editProject, setEditProject] = useState(false);

  // Crossing the mobile breakpoint can leave `tab` holding a value the other
  // tab set does not have (e.g. "Board" on mobile). Fall back rather than
  // rendering whatever the final else-branch happens to be.
  const tabs = mobile ? MOBILE_TABS : TABS;
  const tab = tabs.includes(rawTab) ? rawTab : tabs[0];

  const done = items.filter((i) => i.status === "done").length;
  const percent = pct(done, items.length);
  const due = dueLabel(project?.due, "ends ");
  const sortedPhases = [...phases].sort((a, b) => a.position - b.position);
  const phaseById = new Map<string, Phase>(phases.map((p) => [p.id, p]));

  const { openItem, openNew } = useTaskEditor();

  const PRIORITY_RANK: Record<string, number> = { urgent: 0, high: 1, medium: 2, low: 3 };
  const compareCards = (a: Item, b: Item): number => {
    if (sortBy === "due") return (a.due || "9999-99-99").localeCompare(b.due || "9999-99-99");
    if (sortBy === "priority") return PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority];
    return (a.position ?? 0) - (b.position ?? 0);
  };

  /**
   * A drop that knows where it landed. Same column means reorder; a different
   * column means move, and the insertion index still applies — so dragging
   * across and dropping halfway down puts it halfway down.
   *
   * Only wired while sorting is Manual: under Due or Priority the position a
   * card is dropped at would be overwritten by the sort on the next render,
   * which reads as the drag having failed.
   */
  function dropAt(item: Item, columnKey: string, index: number) {
    const isPhase = group === "phase";
    const keyOf = (i: Item) => (isPhase ? i.phase : i.status);
    const from = keyOf(item);

    // Moving between status columns still has to clear the dependency guard.
    if (!isPhase && columnKey !== from && !guard(item, columnKey as ItemStatus)) return;

    // The board draws Todoist subtasks inside their parent's card, so the gap
    // `index` counts only top-level cards; the column here has to match.
    const nest = todoistNesting(filtered, (c, p) => keyOf(c) === keyOf(p));
    const column = filtered
      .filter((i) => keyOf(i) === columnKey && !nest.isNested(i))
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    const rest = column.filter((i) => i.id !== item.id);

    // `index` is a gap in the column as drawn, which still includes the card
    // being dragged. Removing it shifts every gap below it up by one, so a
    // downward move within the same column has to compensate.
    const currentIndex = column.findIndex((i) => i.id === item.id);
    const at = currentIndex >= 0 && currentIndex < index ? index - 1 : index;

    const next = [...rest];
    next.splice(Math.max(0, Math.min(at, next.length)), 0, item);

    if (columnKey !== from) {
      updateItem.mutate({
        id: item.id,
        ...(isPhase ? { phase: columnKey } : { status: columnKey }),
      } as never);
      // A subtask lives in its parent's section, as in Todoist, so moving the
      // card moves what is drawn inside it. Status is each task's own.
      if (isPhase) {
        for (const d of nest.descendantsOf(item)) updateItem.mutate({ id: d.id, phase: columnKey } as never);
      }
    }
    next.forEach((it, n) => {
      if ((it.position ?? 0) !== n) updateItem.mutate({ id: it.id, position: n } as never);
    });
  }

  /** Swap a card with its neighbour, then renumber the column 0..n-1. */
  function reorderCard(item: Item, dir: -1 | 1) {
    const keyOf = (i: Item) => (group === "phase" ? i.phase : i.status);
    const nest = todoistNesting(filtered, (c, p) => keyOf(c) === keyOf(p));
    const col = filtered
      .filter((i) => keyOf(i) === keyOf(item) && !nest.isNested(i))
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    const at = col.findIndex((i) => i.id === item.id);
    const to = at + dir;
    if (at < 0 || to < 0 || to >= col.length) return;
    const next = [...col];
    [next[at], next[to]] = [next[to], next[at]];
    next.forEach((it, n) => {
      if ((it.position ?? 0) !== n) updateItem.mutate({ id: it.id, position: n } as never);
    });
  }

  /** Where a deleted phase's items go: the phase before it, else the one after. */
  function nearestPhase(ph: Phase): Phase | undefined {
    const i = sortedPhases.findIndex((x) => x.id === ph.id);
    return sortedPhases[i - 1] ?? sortedPhases[i + 1];
  }

  /** §8.2 — a project needs at least one phase, so the last one can't go. */


  // Dependency guard — refuse to advance an item past `waiting` while its
  // prerequisite is still open, and explain why (handoff §2.6). Preserved.
  function guard(it: Item, next: ItemStatus): boolean {
    const prereq = it.dependsOn ? items.find((x) => x.id === it.dependsOn) : undefined;
    const blockedByDep = prereq && prereq.status !== "done";
    if (blockedByDep && STATUS_ORDER.indexOf(next) > STATUS_ORDER.indexOf("waiting")) {
      toast(`Waiting on "${prereq!.title}" first`);
      return false;
    }
    return true;
  }
  function moveStatus(it: Item, dir: -1 | 1) {
    const idx = STATUS_ORDER.indexOf(it.status);
    const next = STATUS_ORDER[Math.min(STATUS_ORDER.length - 1, Math.max(0, idx + dir))];
    if (next === it.status || !guard(it, next)) return;
    setStatus.mutate({ id: it.id, status: next });
  }
  function dropStatus(it: Item, next: ItemStatus) {
    if (next === it.status || !guard(it, next)) return;
    setStatus.mutate({ id: it.id, status: next });
  }

  // Phase reorder: swap with neighbor, then renormalize the whole list to
  // 0..n-1 in one pass so duplicate positions can't accrue (handoff §3.3).

  const filtered = items
    .filter((i) => matchesQuery(i.title, query))
    .filter((i) => showCompleted || i.status !== "done");

  // ---- Publish header chrome (title, meta, actions, view tabs) ------------
  const tabsNode = <HeaderTabs value={tab} options={tabs.map((t) => [t, t])} onChange={setTab} />;
  const projectSeconds = useMemo(
    () => entries.filter((e) => e.project === id).reduce((n, e) => n + (e.durationSeconds || 0), 0),
    [entries, id],
  );
  const budget = useMemo(
    () => (project ? budgetStatus(project, entries) : null),
    [project, entries],
  );

  const actionsNode = mobile ? (
    <button style={secondaryBtn} onClick={() => setEditProject(true)}>Edit</button>
  ) : (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 14px", ...card, borderRadius: 999, boxShadow: "none" }}>
        <span style={{ fontSize: 12.5, color: "var(--ink3)" }}>{done}/{items.length}</span>
        <div style={{ width: 70 }}><ProgressBar value={percent} color={project?.color || "var(--accent)"} /></div>
        <span className="mono" style={{ fontSize: 12.5 }}>{percent}%</span>
      </div>
      {/* Time logged against this project, so the plan and the hours sit side
          by side rather than a page apart. */}
      {projectSeconds > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 14px", ...card, borderRadius: 999, boxShadow: "none" }}>
          <span className="mono" style={{ fontSize: 12.5 }}>{hours(projectSeconds)} h</span>
          {budget && (
            <span style={{ fontSize: 11.5, color: budget.ratio > 1 ? "var(--bad)" : "var(--muted)" }}>
              of {budget.budgetHours}
            </span>
          )}
        </div>
      )}
      {project?.todoistId && (
        <button style={secondaryBtn} onClick={() => openNew(project.id)} title="Quick add a task to this project in Todoist (Q)">
          + Add task
        </button>
      )}
      <button style={secondaryBtn} onClick={() => setEditProject(true)}>Edit</button>
    </>
  );
  useChrome(
    {
      title: project?.name || "Loading…",
      subtitle: project ? `${done}/${items.length} done · ${percent}%${due.text ? " · " + due.text : ""}` : "",
      back: { to: "/projects", label: "Projects" },
      actions: actionsNode,
      tabs: tabsNode,
    },
    [project?.id, done, items.length, percent, tab, group, mobile]
  );

  if (!project) return <div style={{ padding: 36, color: "var(--muted)" }}>Loading…</div>;

  // ---- Board columns for the current grouping (handoff §2) ----------------
  const phaseColumns: Column[] = sortedPhases.map((p) => ({ key: p.id, name: p.name, dot: p.color }));
  const statusColumns: Column[] = STATUS_ORDER
    .filter((s) => showCompleted || s !== "done")
    .map((s) => ({ key: s, name: STATUS_META[s].label, dot: STATUS_META[s].dot }));

  const renderPhaseHeader = (col: Column, count: number) => {
    const ph = phaseById.get(col.key);
    if (!ph) return null;
    return (
      <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0, gap: 2 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: col.dot, flex: "none" }} />
        {(
          <span style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{col.name}</span>
        )}
        <span className="mono" style={{ fontSize: 11, color: "var(--muted2)" }}>{count}</span>

      </div>
      {ph.description && (
        <div style={{ fontSize: 11.5, color: "var(--muted)", marginLeft: 16, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {ph.description}
        </div>
      )}
      </div>
    );
  };


  return (
    <div className={mobile ? "push-in" : undefined} style={{ minWidth: 0 }}>
      {tab === "Overview" && (
        <div style={{ padding: mobile ? "14px 16px 0" : "18px 36px 0" }}>
          <Segmented value={overview} onChange={setOverview} options={OVERVIEW_SEGMENTS} />
        </div>
      )}
      <div style={{ padding: mobile ? "14px 16px 90px" : "20px 36px 90px", minWidth: 0 }}>
        {tab === (mobile ? "Items" : "Board") ? (
          <>
            {/* Board controls: grouping toggle + add buttons (handoff §2.3) */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
              <Segmented value={group} onChange={(v) => setGroup(v as "phase" | "status")} options={[["phase", "Sections"], ["status", "Status"]]} />
              {/* The cards dropped their type labels for the glyph alone, so the
                  key is stated once here rather than on every card. */}
              <span style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 11.5, color: "var(--muted2)" }}>
                {(["task", "deliverable", "milestone"] as const).map((t) => (
                  <span key={t} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                    <span style={{ color: TYPE_META[t].color }}>{TYPE_META[t].glyph}</span>
                    {TYPE_META[t].label}
                  </span>
                ))}
              </span>
              <button
                onClick={() => setShowCompleted(!showCompleted)}
                title="Also set in the Grid toolbar and Settings › Appearance"
                style={{
                  padding: "5px 11px", borderRadius: 999, cursor: "pointer",
                  border: `1px solid ${showCompleted ? "var(--accentLine)" : "var(--line2)"}`,
                  background: showCompleted ? "var(--accentTint)" : "var(--surface)",
                  color: showCompleted ? "var(--accent)" : "var(--muted)",
                  fontSize: 11.5, fontWeight: 500,
                }}
              >
                Completed {showCompleted ? "shown" : "hidden"}
              </button>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "var(--muted)" }}>
                Sort
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as "manual" | "due" | "priority")}
                  style={{ padding: "5px 9px", borderRadius: 999, border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink2)", fontSize: 12, cursor: "pointer" }}
                >
                  <option value="manual">Manual</option>
                  <option value="due">Due date</option>
                  <option value="priority">Priority</option>
                </select>
              </label>
              {/* No add buttons: sections and tasks are created in Todoist and
                  arrive on the next sync. A button here could only ever show a
                  message saying so. */}
            </div>

            {group === "phase" ? (
              phaseColumns.length === 0 ? (
                <EmptyPhases />
              ) : (
                <Kanban
                  items={filtered}
                  columns={phaseColumns}
                  columnOf={(i) => i.phase}
                  onDropIn={(i, phaseId) => {
                    updateItem.mutate({ id: i.id, phase: phaseId });
                    const nest = todoistNesting(filtered, (c, p) => c.phase === p.phase);
                    for (const d of nest.descendantsOf(i)) updateItem.mutate({ id: d.id, phase: phaseId });
                  }}
                  secondary={(i) => (i.status === "todo" ? null : <StatusPill status={i.status} />)}
                  subCounts={subCounts}
                  parentOf={parentOf}
                  onComplete={(i) => toggle(i)}
                  onDefer={(i) => updateItem.mutate({ id: i.id, due: plusWeek(i.due) } as never)}
                  compare={compareCards}
                  onReorder={sortBy === "manual" ? reorderCard : undefined}
                  onDropAt={sortBy === "manual" ? dropAt : undefined}
                  onToggle={(i) => toggle(i)}
                  renderColumnHeader={renderPhaseHeader}
                  columnWidth={mobile ? 232 : 288}
                  onOpen={openItem}
                  mobile={mobile}
                />
              )
            ) : (
              <Kanban
                items={filtered}
                columns={statusColumns}
                columnOf={(i) => i.status}
                onDropIn={(i, s) => dropStatus(i, s as ItemStatus)}
                secondary={(i) => { const ph = phaseById.get(i.phase); return <PhaseChip name={ph?.name} color={ph?.color} />; }}
                subCounts={subCounts}
                parentOf={parentOf}
                onComplete={(i) => toggle(i)}
                onDefer={(i) => updateItem.mutate({ id: i.id, due: plusWeek(i.due) } as never)}
                compare={compareCards}
                onReorder={sortBy === "manual" ? reorderCard : undefined}
                onDropAt={sortBy === "manual" ? dropAt : undefined}
                onToggle={(i) => toggle(i)}
                columnWidth={mobile ? 232 : 272}
                onOpen={openItem}
                onMove={moveStatus}
                mobile={mobile}
              />
            )}
          </>
        ) : tab === "Grid" ? (
          <GridTab
            items={filtered}
            phases={sortedPhases}
            subCounts={subCounts}
            onOpen={openItem}
            onToggle={(i) => toggle(i)}
            onStatusChange={(i, status) => updateItem.mutate({ id: i.id, status })}
            onDueChange={(i, due) => updateItem.mutate({ id: i.id, due: due || null } as any)}
            mobile={mobile}
          />
        ) : tab === "Timeline" ? (
          <TimelineTab phases={sortedPhases} items={filtered} subCounts={subCounts} onOpen={openItem} mobile={mobile} />
        ) : tab === "Calendar" ? (
          <ProjectCalendarTab items={filtered} projectId={project.id} mobile={mobile} />
        ) : mobile ? (
          <ClientReportTab project={project} phases={sortedPhases} items={items} mobile={mobile} />
        ) : tab === "Notes" ? (
          <NotesTab projectId={project.id} mobile={mobile} />
        ) : overview === "definition" ? (
          <AboutTab project={project} phases={sortedPhases} items={items} subtasks={subs} onEdit={() => setEditProject(true)} mobile={mobile} startEditing={openDefinition} />
        ) : overview === "structure" ? (
          <>
            <div style={{ marginBottom: 14 }}>
              <Segmented value={structureView} onChange={setStructureView} options={[["structure", "Structure"], ["matrix", "Phase × status"]]} />
            </div>
            {structureView === "structure" ? (
              <StructureTab phases={sortedPhases} items={filtered} onOpen={openItem} mobile={mobile} />
            ) : (
              <MatrixTab phases={sortedPhases} items={filtered} onOpen={openItem} mobile={mobile} />
            )}
          </>
        ) : overview === "analytics" ? (
          <AnalyticsTab project={project} phases={sortedPhases} items={items} onOpen={openItem} mobile={mobile} />
        ) : (
          <ClientReportTab project={project} phases={sortedPhases} items={items} mobile={mobile} />
        )}
      </div>
      {editProject && (
        <ProjectModal project={project} allProjects={allProjects} onClose={() => setEditProject(false)} />
      )}
    </div>
  );
}

function Segmented({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: [string, string][] }) {
  return (
    <div style={{ display: "flex", gap: 0, padding: 3, background: "var(--tint)", borderRadius: 999 }}>
      {options.map(([v, label]) => {
        const on = v === value;
        return (
          <button key={v} onClick={() => onChange(v)} style={{
            padding: "6px 14px", borderRadius: 999, fontSize: 12.5, cursor: "pointer", border: "none",
            background: on ? "var(--surface)" : "transparent", color: on ? "var(--accent)" : "var(--ink3)", fontWeight: on ? "var(--fw-title)" : "var(--fw-medium)",
            boxShadow: on ? "var(--shadowCard)" : "none",
          }}>{label}</button>
        );
      })}
    </div>
  );
}

function EmptyPhases() {
  return (
    <div style={{ padding: "34px 20px", textAlign: "center", color: "var(--muted)", fontSize: 13 }}>
      No sections yet. Sections come from Todoist — add one there and it appears on the next sync.
    </div>
  );
}

const primaryBtn: CSSProperties = { padding: "7px 13px", borderRadius: 999, border: "none", background: "var(--accent)", color: "var(--onAccent)", fontSize: 12.5, fontWeight: 500, cursor: "pointer" };
const secondaryBtn: CSSProperties = { padding: "7px 13px", borderRadius: 999, border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink2)", fontSize: 12.5, cursor: "pointer" };
